/**
 * Pure helpers for the Schedule-context banner in StrategyPositionsModal.
 * Kept small and pure so they're easy to unit-test without rendering the modal.
 *
 * Phase B trimmed the rows-aggregation helpers (groupByStrategy, aggregateChip,
 * computeScheduleStats) — those responsibilities moved server-side.
 */
import { ENTRY_STATUS, INTRADAY_EXIT_MODE } from '../constants/tradingConstants';
import { classifyTradingType } from './strategy/strategyDisplayHelpers';
import type { ScheduleRow, ScheduleSummaryTotals } from '../types/schedule';

/**
 * Format a scheduled entry → exit time range for display in schedule tables.
 * For NEXT_DAY_BTST strategies, appends "(next day)" so the reader doesn't
 * mis-read "09:44 → 09:30" as a same-day cycle. Returns "—" only when neither
 * time is set.
 */
export function formatTimeRange(
  entry: string | null | undefined,
  exit: string | null | undefined,
  intradayExitMode: string | null | undefined,
): string {
  if (entry && exit) {
    const suffix = intradayExitMode === INTRADAY_EXIT_MODE.NEXT_DAY_BTST ? ' (next day)' : '';
    return `${entry} → ${exit}${suffix}`;
  }
  return entry || exit || '—';
}

/**
 * Compact UPPERCASE label combining trading type, intraday exit mode, and
 * (for POSITIONAL) the entry/exit TDBE (trading-days-before-expiry).
 *
 * Thin wrapper over the canonical `classifyTradingType` classifier in
 * `strategyDisplayHelpers.ts` (single source of truth — see that file for
 * the rule table). This wrapper renders the classifier's discriminated
 * enum as UPPERCASE labels used on schedule + marketplace surfaces:
 *
 *   classifier 'INTRADAY'   → "INTRADAY"
 *   classifier 'BTST_STBT'  → "BTST/STBT"
 *   classifier 'POSITIONAL' → "POSITIONAL"
 *   classifier null         → raw tradingType (forward-compat for unknown
 *                              future values; "" when tradingType is missing)
 *
 * TDBE args are optional: callers without TDBE (the schedule surfaces)
 * default to undefined → classifier falls through to 'POSITIONAL' for
 * positional strategies, preserving legacy 2-arg behaviour.
 */
export function formatTradingType(
  tradingType: string | null | undefined,
  intradayExitMode: string | null | undefined,
  entryTdbe?: number | null,
  exitTdbe?: number | null,
): string {
  if (!tradingType) return '';
  const mode = classifyTradingType({
    tradingType: tradingType || undefined,
    intradayExitMode: intradayExitMode || undefined,
    entryTradingDaysBeforeExpiry: entryTdbe ?? undefined,
    exitTradingDaysBeforeExpiry: exitTdbe ?? undefined,
  });
  if (mode === 'INTRADAY') return 'INTRADAY';
  if (mode === 'BTST_STBT') return 'BTST/STBT';
  if (mode === 'POSITIONAL') return 'POSITIONAL';
  // Unknown trading_type — fall through to the raw value (existing behaviour).
  return tradingType;
}

export interface FillProgress {
  filled: number;
  rejected: number;
  cancelled: number;
  pending: number;
  total: number;
}

/**
 * Count how each leg's entry resolved, used by the modal's banner.
 * REJECTED (broker refused the order) and CANCELLED (order placed but never
 * filled, then auto-cancelled at EOD) are distinct buckets — conflating them
 * mislabels never-filled trigger orders as broker rejections.
 */
export function computeFillProgress(rows: ScheduleRow[]): FillProgress {
  let filled = 0;
  let rejected = 0;
  let cancelled = 0;
  let pending = 0;
  for (const r of rows) {
    if (r.entry_status === ENTRY_STATUS.COMPLETE) {
      filled++;
    } else if (r.entry_status === ENTRY_STATUS.REJECTED) {
      rejected++;
    } else if (r.entry_status === ENTRY_STATUS.CANCELLED) {
      cancelled++;
    } else {
      pending++;
    }
  }
  return { filled, rejected, cancelled, pending, total: rows.length };
}

/**
 * Colour picker: any rejected ⇒ red, any cancelled ⇒ gray (never filled, not
 * an error), any pending ⇒ yellow, all filled ⇒ green.
 */
export function fillProgressVariant(p: FillProgress): 'green' | 'yellow' | 'red' | 'gray' {
  if (p.rejected > 0) return 'red';
  if (p.cancelled > 0) return 'gray';
  if (p.pending > 0) return 'yellow';
  return 'green';
}

/** Distinct broker labels from per-leg rows. */
export function collectBrokerLabels(rows: ScheduleRow[]): string[] {
  const seen: Record<string, true> = {};
  const out: string[] = [];
  for (const r of rows) {
    if (!r.client_id) continue;
    const label = `${(r.broker || '').toUpperCase()} · ${r.client_id}`;
    if (seen[label]) continue;
    seen[label] = true;
    out.push(label);
  }
  return out;
}

/** Display up to `max` labels, collapsing overflow to "+ N more". */
export function formatBrokerList(labels: string[], max = 3): string | null {
  if (labels.length === 0) return null;
  if (labels.length <= max) return labels.join(' · ');
  const head = labels.slice(0, max).join(' · ');
  return `${head} · + ${labels.length - max} more`;
}

function hhmmssToSeconds(hms: string): number | null {
  const parts = hms.split(':').map(Number);
  if (parts.length < 2 || parts.some(isNaN)) return null;
  return (parts[0] || 0) * 3600 + (parts[1] || 0) * 60 + (parts[2] || 0);
}

function hhmmToSeconds(hm: string): number | null {
  const parts = hm.split(':').map(Number);
  if (parts.length !== 2 || parts.some(isNaN)) return null;
  return parts[0] * 3600 + parts[1] * 60;
}

/**
 * Compact countdown in h/m to a same-day target time. Returns null when
 * the target is in the past (or unparsable). Does NOT cross calendar day —
 * callers handle BTST (target on a different date) separately.
 */
export function formatCountdown(nowIstHms: string | null, targetHhmm: string | null): string | null {
  if (!nowIstHms || !targetHhmm) return null;
  const now = hhmmssToSeconds(nowIstHms);
  const target = hhmmToSeconds(targetHhmm);
  if (now == null || target == null) return null;
  const delta = target - now;
  if (delta <= 0) return null;
  const h = Math.floor(delta / 3600);
  const m = Math.floor((delta % 3600) / 60);
  return h > 0 ? `${h}h ${m}m` : `${m}m`;
}

/**
 * SL placements still awaiting morning re-placement, suppressed when the day
 * already has critical issues (any entry rejection or SL failure). Mirrors the
 * mutual exclusivity that the hero used to enforce inline: red issues take
 * precedence and the yellow signal stays hidden so it doesn't compete for
 * attention.
 */
export function effectiveSlPending(
  totals: ScheduleSummaryTotals | null | undefined,
): number {
  if (!totals) return 0;
  if (totals.rejected + totals.sl_rejected > 0) return 0;
  return Math.max(0, totals.sl_pending);
}